import { CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Alert, App, Button, Card, Col, Descriptions, Drawer, Input, List, Modal, Row, Space, Statistic, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { api, unwrap, type Schemas } from "@/api/client";
import { useErrorText } from "@/api/errors";
import { useAuth } from "@/auth/AuthProvider";
import { useI18n, type MessageKey } from "@/i18n";
import { CLIENT_PAGING } from "@/components/list/useListPage";
import { FillCostsButton } from "./FillCosts";

type Item = Schemas["BooksChecklist"]["blockers"][number];

const monthLabel = (m: string) => dayjs(`${m}-01`).format("MMMM YYYY");

/**
 * Keuangan > Tutup buku: months close in order once over. A close keeps the
 * month's end and locks its dates; the owner can reopen the latest one.
 */
export function Books() {
  const { t, formatDate, formatDateTime } = useI18n();
  const { me, can } = useAuth();
  const { message, modal } = App.useApp();
  const errorText = useErrorText();
  const queryClient = useQueryClient();
  const tz = me?.workspace?.timezone;
  const manage = can("finance.manage");
  const [closing, setClosing] = useState<string | null>(null);
  const [viewing, setViewing] = useState<string | null>(null);
  const periods = useQuery({ queryKey: ["books"], queryFn: () => unwrap(api.GET("/v1/finance/periods")) });
  const refresh = () => void queryClient.invalidateQueries({ queryKey: ["books"] });

  const reopen = useMutation({
    mutationFn: (v: { month: string; reason: string }) =>
      unwrap(api.POST("/v1/finance/periods/{month}/reopen", { params: { path: { month: v.month } }, body: { reason: v.reason } })),
    onSuccess: () => {
      void message.success(t("books.reopened"));
      refresh();
    },
    onError: (err) => void message.error(errorText(err)),
  });
  const askReopen = (month: string) => {
    let reason = "";
    modal.confirm({
      title: t("books.reopenTitle", { month: monthLabel(month) }),
      content: (
        <Space orientation="vertical" style={{ width: "100%" }}>
          <Typography.Text>{t("books.reopenBody")}</Typography.Text>
          <Input.TextArea rows={2} maxLength={500} placeholder={t("books.reopenReason")} onChange={(e) => (reason = e.target.value)} />
        </Space>
      ),
      okText: t("books.reopen"),
      okButtonProps: { danger: true },
      cancelText: t("common.cancel"),
      onOk: () => (reason.trim() ? reopen.mutateAsync({ month, reason: reason.trim() }) : Promise.reject(void message.error(t("books.reasonRequired")))),
    });
  };

  const through = periods.data?.closedThrough;
  return (
    <>
      <Typography.Title level={2} style={{ margin: 0, marginBottom: 8, fontSize: 28 }}>
        {t("books.title")}
      </Typography.Title>
      <Typography.Paragraph type="secondary">{t("books.help")}</Typography.Paragraph>
      {periods.data && (
        <Alert
          type={through ? "success" : "info"}
          showIcon
          style={{ marginBottom: 16 }}
          title={through ? t("books.closedThrough", { date: formatDate(through) }) : t("books.neverClosed")}
        />
      )}
      <Table
        rowKey="month"
        loading={periods.isPending}
        dataSource={periods.data?.months ?? []}
        pagination={false}
        columns={[
          { title: t("books.month"), dataIndex: "month", render: (m: string) => <Typography.Text strong>{monthLabel(m)}</Typography.Text> },
          {
            title: t("common.status"),
            dataIndex: "status",
            width: 140,
            render: (s: "current" | "open" | "closed") => <Tag color={{ current: "blue", open: "default", closed: "green" }[s]}>{t(`books.status.${s}`)}</Tag>,
          },
          {
            title: t("books.closedBy"),
            width: 260,
            render: (_, p) => (p.closedAt ? `${p.closedName} · ${formatDateTime(p.closedAt, tz)}` : "—"),
          },
          {
            title: "",
            width: 300,
            render: (_, p) => (
              <Space>
                {p.canClose && manage && (
                  <Button size="small" type="primary" onClick={() => setClosing(p.month)}>
                    {t("books.close")}
                  </Button>
                )}
                {p.status === "closed" && (
                  <Button size="small" onClick={() => setViewing(p.month)}>
                    {t("books.view")}
                  </Button>
                )}
                {p.canReopen && (
                  <Button size="small" danger onClick={() => askReopen(p.month)}>
                    {t("books.reopen")}
                  </Button>
                )}
              </Space>
            ),
          },
        ]}
      />
      {closing && (
        <CloseModal
          month={closing}
          onClose={(done) => {
            setClosing(null);
            refresh();
            if (done) setViewing(done);
          }}
        />
      )}
      <PeriodDrawer month={viewing} onClose={() => setViewing(null)} />
    </>
  );
}

function CheckRow({ item, block }: { item: Item; block: boolean }) {
  const { t, formatNumber } = useI18n();
  const ok = item.count === 0;
  const icon = ok ? (
    <CheckCircleFilled style={{ color: "var(--ant-color-success)" }} />
  ) : block ? (
    <CloseCircleFilled style={{ color: "var(--ant-color-error)" }} />
  ) : (
    <ExclamationCircleFilled style={{ color: "var(--ant-color-warning)" }} />
  );
  return (
    <List.Item>
      <Space align="start">
        {icon}
        <span>
          {t(`books.check.${item.key}` as MessageKey, { n: formatNumber(item.count) })}
          {!ok && (
            <Typography.Text type="secondary" style={{ display: "block", fontSize: 12 }}>
              {t(`books.check.${item.key}.help` as MessageKey)}
            </Typography.Text>
          )}
        </span>
      </Space>
    </List.Item>
  );
}

function CloseModal({ month, onClose }: { month: string; onClose: (closed: string | null) => void }) {
  const { t } = useI18n();
  const { message } = App.useApp();
  const errorText = useErrorText();
  const [note, setNote] = useState("");
  const path = { params: { path: { month } } };
  const check = useQuery({ queryKey: ["books", "check", month], queryFn: () => unwrap(api.GET("/v1/finance/periods/{month}/checklist", path)) });
  const blocked = check.data?.blockers.some((b) => b.count > 0) ?? true;
  const save = useMutation({
    mutationFn: () => unwrap(api.POST("/v1/finance/periods/{month}/close", { ...path, body: { note: note.trim() } })),
    onSuccess: () => {
      void message.success(t("books.closed", { month: monthLabel(month) }));
      onClose(month);
    },
    onError: (err) => void message.error(errorText(err)),
  });
  return (
    <Modal
      open
      title={t("books.closeTitle", { month: monthLabel(month) })}
      okText={t("books.closeOk")}
      cancelText={t("common.cancel")}
      okButtonProps={{ disabled: blocked }}
      confirmLoading={save.isPending}
      onOk={() => save.mutate()}
      onCancel={() => onClose(null)}
      width={620}
      destroyOnHidden
    >
      <Typography.Paragraph type="secondary">{t("books.closeBody")}</Typography.Paragraph>
      {check.data && (
        <>
          <Typography.Text strong>{t("books.blockers")}</Typography.Text>
          <List size="small" dataSource={check.data.blockers} renderItem={(x) => <CheckRow item={x} block />} />
          <Typography.Text strong>{t("books.warnings")}</Typography.Text>
          <List size="small" dataSource={check.data.warnings} renderItem={(x) => <CheckRow item={x} block={false} />} />
          {blocked && <Alert type="error" showIcon style={{ marginTop: 8 }} title={t("books.blocked")} />}
        </>
      )}
      <Input.TextArea style={{ marginTop: 12 }} rows={2} maxLength={500} placeholder={t("books.note")} value={note} onChange={(e) => setNote(e.target.value)} />
    </Modal>
  );
}

function PeriodDrawer({ month, onClose }: { month: string | null; onClose: () => void }) {
  const { t, formatMoney, formatNumber, formatDateTime } = useI18n();
  const { me } = useAuth();
  const tz = me?.workspace?.timezone;
  const period = useQuery({
    queryKey: ["books", "period", month],
    queryFn: () => unwrap(api.GET("/v1/finance/periods/{month}", { params: { path: { month: month! } } })),
    enabled: month !== null,
  });
  const close = period.data?.closes.find((c) => c.status === "closed") ?? period.data?.closes[0];
  const s = close?.summary;
  const whName = new Map((period.data?.stock ?? []).map((x) => [x.warehouseId, x.warehouseName]));
  const stat = (title: string, value: number) => (
    <Col xs={12} md={6}>
      <Statistic title={title} value={formatMoney(value)} />
    </Col>
  );
  return (
    <Drawer open={month !== null} onClose={onClose} size="large" title={month ? t("books.viewTitle", { month: monthLabel(month) }) : ""} destroyOnHidden>
      {s && close && (
        <Space orientation="vertical" size={16} style={{ width: "100%" }}>
          {close.status === "reopened" && <Alert type="warning" showIcon title={t("books.wasReopened", { reason: close.reopenReason })} />}
          <Card size="small" title={t("books.pnl")}>
            <Row gutter={[16, 16]}>
              {stat(t("report.revenue"), s.revenue)}
              {stat(t("report.cost"), -s.cost)}
              {stat(t("books.fees"), s.fees)}
              {stat(t("books.variance"), s.varianceGain - s.varianceLoss)}
              {!!s.damagedLoss && stat(t("report.damaged"), -s.damagedLoss)}
              {!!s.lostLoss && stat(t("report.lost"), -s.lostLoss)}
              {!!s.claimsRecovered && stat(t("report.recovered"), s.claimsRecovered)}
              {stat(t("report.grossProfit"), s.grossProfit)}
              {stat(t("report.afterFees"), s.afterFees)}
              {stat(t("books.net"), s.net)}
            </Row>
            {s.unitsNoCost > 0 && <Alert type="warning" showIcon style={{ marginTop: 12 }} title={t("report.noCost", { n: formatNumber(s.unitsNoCost) })} action={<FillCostsButton />} />}
          </Card>
          <Card size="small" title={t("books.position")}>
            <Row gutter={[16, 16]}>
              {stat(t("books.stockValue"), s.stockValue)}
              {!!s.stockInTransit && stat(t("books.stockInTransit"), s.stockInTransit)}
              {stat(t("books.cash"), s.cashTotal)}
              {stat(t("books.payables"), -s.payables)}
            </Row>
            <Descriptions size="small" column={1} style={{ marginTop: 12 }}>
              {s.warehouses.map((w) => (
                <Descriptions.Item key={w.id} label={whName.get(w.id) ?? w.id}>
                  {formatNumber(w.units)} unit · {formatMoney(w.value)}
                </Descriptions.Item>
              ))}
              {s.cash.map((c) => (
                <Descriptions.Item key={c.id} label={c.name}>
                  {formatMoney(c.balance)}
                </Descriptions.Item>
              ))}
            </Descriptions>
            {s.stockUnitsNoCost > 0 && (
              <Alert type="warning" showIcon style={{ marginTop: 12 }} title={t("books.stockNoCost", { n: formatNumber(s.stockUnitsNoCost) })} />
            )}
          </Card>
          <Card size="small" title={t("books.stock")}>
            <Table
              size="small"
              rowKey={(x) => `${x.warehouseId}|${x.skuId}`}
              dataSource={period.data?.stock ?? []}
              pagination={CLIENT_PAGING}
              columns={[
                { title: t("orders.warehouse"), dataIndex: "warehouseName", width: 150 },
                { title: t("catalog.code"), dataIndex: "skuCode", render: (v: string, x) => `${v} · ${x.skuName}` },
                { title: t("books.qty"), dataIndex: "quantity", width: 90, align: "right", render: (v: number) => formatNumber(v) },
                { title: t("books.unitCost"), dataIndex: "unitCost", width: 130, align: "right", render: (v: number | null) => (v == null ? "—" : formatMoney(v)) },
                { title: t("books.value"), dataIndex: "value", width: 140, align: "right", render: (v: number) => formatMoney(v) },
              ]}
            />
          </Card>
          <Card size="small" title={t("books.history")}>
            <List
              size="small"
              dataSource={period.data?.closes ?? []}
              renderItem={(c) => (
                <List.Item>
                  <Space orientation="vertical" size={0}>
                    <span>
                      {t("books.closedLine", { name: c.closedName, at: formatDateTime(c.closedAt, tz) })}
                      {c.note && ` · ${c.note}`}
                    </span>
                    {c.reopenedAt && (
                      <Typography.Text type="danger">
                        {t("books.reopenedLine", { name: c.reopenedName, at: formatDateTime(c.reopenedAt, tz), reason: c.reopenReason })}
                      </Typography.Text>
                    )}
                    {c.warnings.length > 0 && (
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                        {t("books.closedWithWarnings", { list: c.warnings.map((w) => `${t(`books.check.${w.key}` as MessageKey, { n: w.count })}`).join("; ") })}
                      </Typography.Text>
                    )}
                  </Space>
                </List.Item>
              )}
            />
          </Card>
        </Space>
      )}
    </Drawer>
  );
}
